// AUTHENTICATION IS ASYNCHRONOUS. THE SHELL MUST NOT BLOCK ON IT.
//
// The console rendered a full-screen "Checking session…" and waited on
// GET /api/auth/get-session before drawing anything. Measured against the deployed backend on
// 2026-08-27, unauthenticated (the FLOOR — a real session adds a session-row lookup and the
// org-scope read on top): 0.70 · 0.71 · 0.72 · 1.18 · 0.98 · 2.20 · 0.97 · 0.71 seconds, with a
// 5.6s outlier. So every single page load spent one to two seconds on a blank screen
// re-confirming something that was true a moment earlier and is almost always still true.
//
// That wait bought nothing, because the answer it waits for is not the thing that protects
// anything. THE SERVER IS THE ONLY AUTHORITY: every data call carries the cookie and the backend
// decides, request by request. A dead session is already handled thoroughly and globally by
// lib/sessionExpired.js — one wrapper around globalThis.fetch that catches any 401 from our
// backend, attempts a silent passkey re-auth, and otherwise raises a non-dismissing red
// "Session expired" toast with a Log out action. The blocking gate was a second, slower, weaker
// copy of a check that already happens on every request.
//
// So: remember locally that we WERE authenticated, paint the shell immediately, and revalidate in
// the background. Same shape as the dashboard chrome — the cache exists to paint instantly, the
// authority arrives asynchronously and corrects it.
//
// WHAT THIS MARKER IS NOT: a credential, a token, or a permission. Forging it grants exactly
// nothing — you get an empty shell in which every request 401s and the guard fires. The only
// thing rendered from it is the operator's own last-known name and email, in their own browser,
// in the top bar, for the moment before the real session lands.
//
// The alternatives this deliberately displaces, all of which make the session live LONGER to buy
// the same comfort: raising the session TTL, "remember me", refresh tokens, a keepalive
// heartbeat. Each pays a security cost for a UX problem. This pays none — the expiry can stay as
// short as it likes, because nothing waits on it.

const KEY = 'tkt.session.marker';

/** The last known-good session, or null. Shape: { email, name, at }. */
export const readSessionMarker = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const m = JSON.parse(raw);
    return m && m.email ? m : null;
  } catch {
    // Private mode, disabled storage, or a shape from an older build. An unreadable marker is
    // simply "no marker" — it degrades to the old blocking check, never to a broken boot.
    return null;
  }
};

export const writeSessionMarker = (user) => {
  if (!user?.email) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ email: user.email, name: user.name || '', at: Date.now() }));
  } catch { /* convenience only */ }
};

/**
 * Forget it. Called from BOTH ends of every way a session can end — the explicit Sign out, and
 * the 401 guard's Log out. The second is the one that makes this deterministic: without it, a
 * reload after a dead session would boot optimistically into the shell, 401, and offer Log out
 * again — a loop that looks exactly like the console being broken.
 */
export const clearSessionMarker = () => {
  try { localStorage.removeItem(KEY); } catch { /* nothing to forget */ }
};
